import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, useToast } from '@morressier/alembic';

import { FormProvider, useForm } from 'react-hook-form';
import { IDecisionModalProps } from '../decisionModalHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport } from '@fortawesome/pro-light-svg-icons';
import FormFieldTextArea from 'src/components/FormField/FormFieldTextArea';
import { useDeclineTransfer } from 'src/api/commands.js';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider.js';
import { zodResolver } from '@hookform/resolvers/zod';
import { declineTransferValidator } from '@publishing-engine/domain/validation/index.js';

export const DeclineTransferModal = ({
  onClose,
  submissionId,
  submissionContextId
}: IDecisionModalProps) => {
  const toast = useToast();
  const { refetchSubmission, transferOffer, organizationId } =
    useSubmissionData();
  const methods = useForm({
    defaultValues: {
      declineReason: ''
    },
    resolver: zodResolver(declineTransferValidator)
  });

  const { handleSubmit } = methods;

  const declineTransferMutation = useDeclineTransfer(
    submissionId,
    submissionContextId,
    refetchSubmission,
    organizationId
  );

  const onSubmit = async (formPayload: { declineReason: string }) => {
    const res = await declineTransferMutation.mutateAsync(formPayload);

    if (res.isSuccess) {
      const reason = transferOffer?.transferReason;

      onClose();
      toast({
        variant: 'success',
        title: 'Transfer Declined',
        description: `Your Submission was rejected${reason && ` as ${reason}`}`,
        isClosable: true
      });
    } else {
      toast({
        variant: 'error',
        title: 'Error declining transfer',
        description:
          res.message?.errors
            ?.map((err: { reason: string }) => err.reason)
            .join(', ') || 'Try again later!'
      });
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      width="755px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex flexDirection="column" gap={8}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDirection="column" gap={8} padding={8} width="100%">
              <Flex>
                <FontAwesomeIcon fontSize={32} icon={faFileImport} />
              </Flex>
              <Flex flexDirection="column" gap={6}>
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingGroup">Decline Transfer?</Text>
                  <Text textStyle="textBody">
                    If you decline this offer, this Submission will be rejected.
                  </Text>
                </Flex>
                <FormFieldTextArea
                  config={{
                    name: 'declineReason',
                    component: 'Custom:TextArea',
                    params: {
                      labels: {
                        main: 'Reason for Declining',
                        subtitle:
                          'Providing a reason helps us improve future transfer offers.'
                      },
                      validations: {
                        required: true,
                        kind: 'string',
                        maxLength: 1000
                      }
                    },
                    caseDataFields: {
                      main: 'declineReason'
                    }
                  }}
                  placeholder="Provide a reason"
                />
              </Flex>

              <Flex gap={2} justifyContent="flex-end">
                <Button
                  variant="secondary"
                  onClick={onClose}
                  borderRadius="small"
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" borderRadius="small">
                  Confirm Decision
                </Button>
              </Flex>
            </Flex>
          </form>
        </FormProvider>
      </Flex>
    </Modal>
  );
};
